import { useCallback, useEffect, useRef, useMemo } from 'react';
import type { LevelData, LevelCardState, LevelCardEffect, PuzzleDefinition } from '../types';
import { sliceSolution } from '../puzzles/multiPuzzleUtils';
import './LevelCardGrid.css';

/* ---------- colors (match existing CSS look) ---------- */

const COL_CELL_BG = 'rgba(74, 32, 8, 0.18)';
const COL_CELL_HOVER = 'rgba(74, 32, 8, 0.28)';
const COL_BORDER = 'rgba(74, 32, 8, 0.15)';
const COL_FILLED = '#4a2008';
const COL_LABEL = '#a08060';

/* ---------- hit-testing ---------- */

function puzzleIndexFromPixel(
  px: number,
  py: number,
  canvas: HTMLCanvasElement,
  puzzles: PuzzleDefinition[],
  solRows: number,
  solCols: number,
): number {
  const rect = canvas.getBoundingClientRect();
  const x = (px - rect.left) / rect.width;
  const y = (py - rect.top) / rect.height;
  const col = x * solCols;
  const row = y * solRows;
  for (let i = 0; i < puzzles.length; i++) {
    const p = puzzles[i];
    if (col >= p.col && col < p.col + p.width && row >= p.row && row < p.row + p.height) {
      return i;
    }
  }
  return -1;
}

/* ---------- component ---------- */

export type CellVisualState = 'locked' | 'available' | 'completed';

export interface LevelCardGridProps {
  level: LevelData;
  state: LevelCardState;
  /** Visual effects applied to the card */
  effects?: LevelCardEffect[];
  /** Which puzzle indices have their solution visible */
  revealedCells: ReadonlySet<number>;
  /** Called when a cell is clicked */
  onCellClick?: (cellIndex: number) => void;
  /** Custom labels for cells (by puzzle index). Falls back to index+1 for unrevealed. */
  cellLabels?: ReadonlyMap<number, string>;
  /** Visual state per cell (by puzzle index). Defaults to 'available'. */
  cellStates?: ReadonlyMap<number, CellVisualState>;
  /** Content rendered above the grid */
  headerSlot?: React.ReactNode;
  /** Override what's shown in the name slot */
  nameSlot?: React.ReactNode;
  /**
   * When false, renders the full level solution as a single pixel-art image
   * without sub-puzzle borders, numbers, or per-region backgrounds.
   * Default: true (show sub-level breakdown).
   */
  showSubLevels?: boolean;
  /**
   * Explicit card width in pixels. When omitted, the card fills its parent.
   * Height is derived from the card's aspect ratio.
   */
  size?: number;
  /** Extra CSS class on the root element */
  className?: string;
}

export function LevelCardGrid({
  level,
  state,
  effects,
  revealedCells,
  onCellClick,
  cellLabels,
  cellStates,
  headerSlot,
  nameSlot,
  showSubLevels = true,
  size,
  className,
}: LevelCardGridProps) {
  const { solution, puzzles, name } = level;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoverRef = useRef(-1);

  const solutionRows = solution.length;
  const solutionCols = solution[0]?.length ?? 0;
  const locked = state === 'locked';
  const completed = state === 'completed';

  const effectiveRevealed = useMemo(() => {
    if (completed) return new Set(puzzles.map((_, i) => i));
    return revealedCells;
  }, [completed, puzzles, revealedCells]);

  // Pre-slice revealed solutions
  const slicedSolutions = useMemo(() => {
    const map = new Map<number, boolean[][]>();
    puzzles.forEach((p, i) => {
      if (effectiveRevealed.has(i)) {
        map.set(i, sliceSolution(solution, p));
      }
    });
    return map;
  }, [puzzles, solution, effectiveRevealed]);

  // Store mutable props for the draw loop
  const propsRef = useRef({ puzzles, solution, solutionRows, solutionCols, effectiveRevealed, slicedSolutions, locked, cellLabels, cellStates, showSubLevels });
  propsRef.current = { puzzles, solution, solutionRows, solutionCols, effectiveRevealed, slicedSolutions, locked, cellLabels, cellStates, showSubLevels };

  // Canvas draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    const draw = () => {
      const p = propsRef.current;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) { raf = requestAnimationFrame(draw); return; }
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cw = w / p.solutionCols;
      const ch = h / p.solutionRows;
      const borderW = Math.max(0.5, Math.min(cw, ch) * 0.04);

      // Clear to transparent (the CSS background shows through for revealed cells)
      ctx.clearRect(0, 0, w, h);

      if (p.locked) { raf = requestAnimationFrame(draw); return; }

      const hover = hoverRef.current;

      // Flat mode: draw the whole solution as one pixel-art image
      if (!p.showSubLevels) {
        ctx.fillStyle = COL_FILLED;
        for (let r = 0; r < p.solutionRows; r++) {
          const row = p.solution[r];
          if (!row) continue;
          for (let c = 0; c < p.solutionCols; c++) {
            if (row[c]) ctx.fillRect(c * cw, r * ch, cw, ch);
          }
        }
        raf = requestAnimationFrame(draw);
        return;
      }

      // Draw each sub-puzzle
      for (let i = 0; i < p.puzzles.length; i++) {
        const pz = p.puzzles[i];
        const x = pz.col * cw;
        const y = pz.row * ch;
        const pw = pz.width * cw;
        const ph = pz.height * ch;
        const revealed = p.effectiveRevealed.has(i);
        const cs = p.cellStates?.get(i) ?? 'available';
        const dimmed = cs === 'locked';

        if (dimmed) ctx.globalAlpha = 0.4;

        if (revealed) {
          // Draw pixel-art solution
          const sol = p.slicedSolutions.get(i);
          if (sol) {
            const rows = sol.length;
            const cols = sol[0]?.length ?? 0;
            const cellW = pw / cols;
            const cellH = ph / rows;
            ctx.fillStyle = COL_FILLED;
            for (let r = 0; r < rows; r++) {
              for (let c = 0; c < cols; c++) {
                if (sol[r][c]) ctx.fillRect(x + c * cellW, y + r * cellH, cellW, cellH);
              }
            }
          }

          // Hover overlay — day label in green
          if (i === hover && !dimmed) {
            const label = p.cellLabels?.get(i);
            if (label) {
              ctx.fillStyle = COL_CELL_HOVER;
              ctx.fillRect(x, y, pw, ph);
              const fontSize = Math.min(pw, ph) * 0.38;
              ctx.font = `800 ${fontSize}px 'Nunito', sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillStyle = '#f0b830';
              ctx.fillText(label, x + pw / 2, y + ph / 2);
            }
          }
        } else {
          // Unrevealed cell background
          ctx.fillStyle = (i === hover && !dimmed) ? COL_CELL_HOVER : COL_CELL_BG;
          ctx.fillRect(x, y, pw, ph);

          // Number label
          const label = p.cellLabels?.get(i) ?? `${i + 1}`;
          const fontSize = Math.min(pw, ph) * 0.38;
          ctx.font = `800 ${fontSize}px 'Nunito', sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = COL_LABEL;
          ctx.fillText(label, x + pw / 2, y + ph / 2);
        }

        // Cell border between sub-puzzles
        ctx.strokeStyle = COL_BORDER;
        ctx.lineWidth = borderW;
        ctx.strokeRect(x, y, pw, ph);

        if (dimmed) ctx.globalAlpha = 1;
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Hit-test for pointer events
  const getIndex = useCallback(
    (e: React.PointerEvent) => {
      const canvas = canvasRef.current;
      if (!canvas || locked) return -1;
      return puzzleIndexFromPixel(e.clientX, e.clientY, canvas, puzzles, solutionRows, solutionCols);
    },
    [puzzles, solutionRows, solutionCols, locked],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => { hoverRef.current = getIndex(e); },
    [getIndex],
  );

  const handlePointerLeave = useCallback(() => { hoverRef.current = -1; }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas || locked) return;
      const idx = puzzleIndexFromPixel(e.clientX, e.clientY, canvas, puzzles, solutionRows, solutionCols);
      if (idx >= 0) onCellClick?.(idx);
    },
    [puzzles, solutionRows, solutionCols, locked, effectiveRevealed, onCellClick],
  );

  const rootCls = [
    'level-card-grid',
    `level-card-grid--${state}`,
    ...(effects ?? []).map(e => `level-card-grid--fx-${e}`),
    className,
  ].filter(Boolean).join(' ');

  const allRevealed = effectiveRevealed.size === puzzles.length;
  const nameLocked = !locked && !completed && !allRevealed && nameSlot === undefined;
  const nameContent = locked
    ? '\u00A0'
    : (nameSlot !== undefined ? nameSlot : name);

  const rootStyle = size != null ? { width: `${size}px` } : undefined;

  return (
    <div className={rootCls} style={rootStyle}>
      {locked && (
        <span className="level-card-grid__lock">{'\u{1F512}'}</span>
      )}
      {headerSlot && <div className="level-card-grid__header">{headerSlot}</div>}
      <div className="level-card-grid__grid-area">
        <div
          className="level-card-grid__grid"
          style={{
            aspectRatio: `${solutionCols} / ${solutionRows}`,
            '--ar': solutionCols / solutionRows,
            ...(locked ? { visibility: 'hidden' } : undefined),
          } as React.CSSProperties}
        >
          <canvas
            ref={canvasRef}
            className="level-card-grid__canvas"
            onClick={handleClick}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerLeave}
          />
        </div>
      </div>

      <span className={`level-card-grid__name${nameContent ? '' : ' level-card-grid__name--empty'}${nameLocked ? ' level-card-grid__name--locked' : ''}`}>
        {nameLocked && <span className="level-card-grid__name-lock">{'\u{1F512}'}</span>}
        <span className={nameLocked ? 'level-card-grid__name-text--locked' : undefined}>
          {nameContent || '\u00A0'}
        </span>
      </span>
    </div>
  );
}
